import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import { Complex } from "@/types/game";

export const useComplexesTableState = (
  complexes: Complex[] | undefined,
  view: "grid" | "list",
) => {
  const [rawSearchValue, setSearchValue] = useState("");
  const searchValue = useDebounce(rawSearchValue, 250, false);
  const [sortingState, setSortingState] = useState<SortingState>([]);

  const filteredComplexes = useMemo(() => {
    if (view !== "list" || !complexes) return complexes;

    const trimmed = searchValue.trim().toLowerCase();
    const filtered = trimmed
      ? complexes.filter((c) => c.name.toLowerCase().includes(trimmed))
      : complexes;

    if (!sortingState.length) return filtered;

    const { id, desc } = sortingState[0];

    const columnKeyMap: Record<string, keyof Complex> = {
      activeEvents: "activeEventsCount",
      scheduledEvents: "scheduledEventsCount",
      fieldCount: "fieldCount",
    };
    const key = columnKeyMap[id] ?? (id as keyof Complex);

    return [...filtered].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      if (key === "fields") {
        aVal = a.fields.length;
        bVal = b.fields.length;
      } else {
        aVal = (a[key] as string | number) ?? "";
        bVal = (b[key] as string | number) ?? "";
      }

      if (aVal < bVal) return desc ? 1 : -1;
      if (aVal > bVal) return desc ? -1 : 1;
      return 0;
    });
  }, [complexes, searchValue, sortingState, view]);

  return {
    filteredComplexes,
    sortingState,
    setSortingState,
    rawSearchValue,
    setSearchValue,
  };
};
